/**
 * Papéis do escritório no frontend — espelho de apps/api/src/core/auth/roles.ts.
 * Os papéis antigos continuam chegando da API; aqui eles viram Sócio/Advogado/
 * Assistente para exibição e para as checagens de tela.
 */
export const OFFICE_ROLES = ['SOCIO', 'ADVOGADO', 'ASSISTENTE', 'AGENTE'] as const
export type OfficeRole = (typeof OFFICE_ROLES)[number]

const LEGACY_ROLES = ['SUPER_ADMIN', 'ADMIN', 'LAWYER', 'ASSISTANT', 'USER', 'CLIENT']

const LEGACY_TO_OFFICE: Record<string, OfficeRole | null> = {
  SUPER_ADMIN: 'SOCIO',
  ADMIN: 'SOCIO',
  LAWYER: 'ADVOGADO',
  ASSISTANT: 'ASSISTENTE',
  USER: 'ASSISTENTE',
  CLIENT: null,
}

export const ROLE_LABELS: Record<OfficeRole, string> = {
  SOCIO: 'Sócio',
  ADVOGADO: 'Advogado',
  ASSISTENTE: 'Assistente',
  AGENTE: 'Agente',
}

export function isOfficeRole(role: string | null | undefined): role is OfficeRole {
  return !!role && (OFFICE_ROLES as readonly string[]).includes(role)
}

export function canonicalRole(role: string | null | undefined): OfficeRole | null {
  if (!role) return null
  if (isOfficeRole(role)) return role
  return LEGACY_TO_OFFICE[role] ?? null
}

export function roleLabel(role: string | null | undefined): string {
  const office = canonicalRole(role)
  if (office) return ROLE_LABELS[office]
  if (role === 'CLIENT') return 'Cliente'
  return role ?? ''
}

/** Mesma regra da API: papel novo atende o antigo equivalente e vice-versa. */
export function hasRole(userRole: string | null | undefined, required: readonly string[]): boolean {
  if (!userRole) return false
  if (required.includes(userRole)) return true
  if (isOfficeRole(userRole)) {
    return required.some((r) => LEGACY_ROLES.includes(r) && canonicalRole(r) === userRole)
  }
  const office = canonicalRole(userRole)
  return !!office && required.some((r) => isOfficeRole(r) && r === office)
}

export const isSocio = (role: string | null | undefined) => canonicalRole(role) === 'SOCIO'

/** Sócio ou advogado (quem trabalha como advogado). */
export const isLawyerLike = (role: string | null | undefined) => {
  const office = canonicalRole(role)
  return office === 'SOCIO' || office === 'ADVOGADO'
}

/** Selo do papel com as cores do tema (aplicar sobre `<Badge variant="outline">`). */
export const ROLE_BADGE_CLASSES: Record<OfficeRole, string> = {
  SOCIO: 'border-transparent bg-primary text-primary-foreground',
  ADVOGADO: 'border-primary/30 bg-primary/10 text-primary',
  ASSISTENTE: 'border-border bg-secondary text-secondary-foreground',
  AGENTE: 'border-dashed border-border bg-background text-muted-foreground',
}

export function roleBadgeClass(role: string | null | undefined): string {
  const office = canonicalRole(role)
  return office ? ROLE_BADGE_CLASSES[office] : 'border-border bg-muted text-muted-foreground'
}

/** Mesma regra do `requirePermission` da API: `*` atende qualquer permissão. */
export function hasPermission(permissions: readonly string[] | null | undefined, required: string): boolean {
  return !!permissions && (permissions.includes('*') || permissions.includes(required))
}

/** Agente (Hermes) usa só a API por chave; não tem interface. */
export const isAgent = (role: string | null | undefined) => canonicalRole(role) === 'AGENTE'
